import React, { useState, useEffect } from 'react';
import '../ImagePrompt.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import checkSVG from '../Iconos/check.svg';
import { Helmet } from 'react-helmet';
import ButtonSidebarReel from './ButtonSidebarReel';
import arrowSVG from '../Iconos/arrow.svg';
import { useHistory } from 'react-router-dom';

const VALID_MINUTES = 35; // Videos are saved for 35 minutes

function VideoPrompt() {
  const { t } = useTranslation();
  const history = useHistory();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [modalMessage, setModalMessage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessStarted, setIsProcessStarted] = useState(false);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      history.push('/PantallaInicial');
    }
  }, [history]);

  useEffect(() => {
    const videoDataJSON = localStorage.getItem('selectedVideo');
    if (videoDataJSON) {
      const videoData = JSON.parse(videoDataJSON);
      const timeNow = new Date().getTime();
      const timeElapsed = (timeNow - videoData.timestamp) / 1000 / 60;

      if (timeElapsed < VALID_MINUTES) {
        setVideoUrl(videoData.video);
      } else {
        localStorage.removeItem('selectedVideo');
      }
    }

    const coverImageDataJSON = localStorage.getItem('selectedCoverImage');
    if (coverImageDataJSON) {
      setCoverImageUrl(JSON.parse(coverImageDataJSON).imageUrl);
    }
  }, []);

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('video', file);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

      // Cambiar el endpoint para que use AWS S3
      const response = await axios.post(`${backendUrl}/upload-reel-video`, formData, {
        headers: {
          'ngrok-skip-browser-warning': 'true', // Evita la advertencia de Ngrok
          'Content-Type': 'multipart/form-data',
        },
      });

      const { videoUrl } = response.data;

      const videoData = {
        video: videoUrl,
        timestamp: new Date().getTime(),
      };
      localStorage.setItem('selectedVideo', JSON.stringify(videoData));
      setVideoUrl(videoUrl);

      setModalMessage('Video subido con éxito');
    } catch (error) {
      setModalMessage('Error al subir el video');
    }
  };

  const handleCoverImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('coverImage', file); // Cambiado de 'image' a 'coverImage'

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

      // Cambiar el endpoint para que use AWS S3
      const response = await axios.post(`${backendUrl}/upload-reel-cover`, formData, {
        headers: {
          'ngrok-skip-browser-warning': 'true', // Evita la advertencia de Ngrok
          'Content-Type': 'multipart/form-data',
        },
      });

      const { coverImageUrl } = response.data;

      const coverImageData = {
        imageUrl: coverImageUrl,
      };
      localStorage.setItem('selectedCoverImage', JSON.stringify(coverImageData));
      setCoverImageUrl(coverImageUrl);

      setModalMessage('Imagen de portada subida con éxito');
    } catch (error) {
      setModalMessage('Error al subir la imagen de portada');
    }
  };

  const handleSaveDraft = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setModalMessage('No estás autenticado.');
      return;
    }

    if (!videoUrl) {
      setModalMessage('No hay video seleccionado.');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

      await axios.post(`${backendUrl}/save-drafts`, {
        userId: localStorage.getItem('userId'),
        videos: [videoUrl]
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setModalMessage('Borrador guardado con éxito');
    } catch (error) {
      setModalMessage('Error al guardar el borrador');
    }
  };

  const handlePreviousPage = () => {
    history.push('/');
  };

  const handleNextPage = async () => {
    setIsGenerating(true);
    setIsProcessStarted(true); // Marcar que el proceso ha comenzado

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('No hay token de autenticación disponible');
      }

      // Restamos los créditos (35 créditos en este caso)
      console.log("Iniciando proceso para restar créditos...");

      const creditosResponse = await axios.post(`${backendUrl}/restar-creditos`, {
        cantidad: 2
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('Respuesta de restar créditos:', creditosResponse.data); // Verificar respuesta

      if (creditosResponse.data.success) {
        console.log('Créditos restados correctamente. Procediendo con el siguiente paso...');
        // Aquí puedes manejar lo que ocurra después, como navegar a otra página
        history.push('/Social-Networking-Reel2');
      } else {
        console.error('Problema al restar créditos:', creditosResponse.data.mensaje);
        alert(creditosResponse.data.mensaje);
      }
    } catch (error) {
      console.error("Error al restar créditos:", error);
      alert('Ha ocurrido un error al restar créditos.');
    } finally {
      setIsGenerating(false);
    }
  };

  const closeModal = () => {
    setModalMessage(null);
  };

  return (
    <div className='container-text'>
      <Helmet>
        <title>Redes Sociales - Reel</title>
      </Helmet>
      <div className='reel-container'>
        {/* Modal */}
        {modalMessage && (
          <div className="modal">
            <div className="modal-content">
              <p>{modalMessage}</p>
              <button className="close-btn" onClick={closeModal}>Aceptar</button>
            </div>
          </div>
        )}

        <div className="image-prompt-container">
          <div className="image-prompt-title">
            <p>{t('Sube tu reel')} <span className="highlight">{t('aquí')}</span>.</p>
          </div>

          <div className="upload-instructions">
            <p>Por favor, sube un video en formato vertical. Solo se permiten videos con una relación de aspecto 9:16, ideal para reels.</p>
          </div>

          <div className="file-upload-container">
            <input
              type="file"
              id="file-upload-video"
              accept="video/*"
              onChange={handleVideoUpload}
              className="file-upload-input"
              style={{ display: 'none' }}
            />
            <label htmlFor="file-upload-video" className="file-upload-label">
              <span>Subir video</span>
            </label>
          </div>

          <div className="file-upload-container">
            <input
              type="file"
              id="file-upload-image"
              accept="image/*"
              onChange={handleCoverImageUpload}
              className="file-upload-input"
              style={{ display: 'none' }}
            />
            <label htmlFor="file-upload-image" className="file-upload-label">
              <span>Subir imagen de portada</span>
            </label>
          </div>

          <div className="main-video-container">
            {videoUrl ? (
              <video controls src={videoUrl} className="main-video" />
            ) : (
              <p>No se ha subido ningún video</p>
            )}
            {coverImageUrl ? (
              <img src={coverImageUrl} alt="Imagen de portada" className="main-cover-image" />
            ) : (
              <p>No se ha subido ninguna imagen de portada</p>
            )}
          </div>

          <div className="image-selection-navigation">
            <button className="image-selection-previous" onClick={handlePreviousPage}>
              <img src={arrowSVG} alt="" className='reverse-arrow' /> {t('Anterior')}
            </button>
            <button className="image-selection-next" onClick={handleNextPage}>
              {t('Siguiente')} <img src={arrowSVG} alt="" className='normal-arrow' />
            </button>
          </div>
        </div>
      </div>
      <ButtonSidebarReel
        onSaveDraft={handleSaveDraft}
        onCropClick={function (): void {
          throw new Error('Function not implemented.');
        }}
        selectedImage={''} />
    </div>
  );
}

export default VideoPrompt;
